import { Loader } from "@mantine/core";
import type { JSX, ReactNode } from "react";
import { ComponentProps, memo, useEffect, useMemo, useRef } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { AutoSizer, List } from "react-virtualized";

import { SearchInput } from "@/components/@custom/SearchInput";
import { omitClassNameFromProps } from "@/utils";
import { cn } from "@/utils/cn";

type ExpeditionQueueStatusBaseProps<T> = {
  loading: boolean;
  slots: {
    search: { props: ComponentProps<typeof SearchInput>; disable?: boolean };
    loader?: { props: ComponentProps<typeof Loader>; wrapper?: ComponentProps<"div"> };
    section?: { props: ComponentProps<"section"> };
    empty?: {
      props: ComponentProps<"div">;
      message?: string;
    };
    alert?: {
      props?: ComponentProps<"div">;
      message: string | ReactNode;
    };
  };
  list: {
    data: T[];
    render: (vehicles: T, index: number, arr: T[]) => ReactNode;
    Wrapper?: (children: ReactNode) => ReactNode;
    filter?: (vehicles: T) => void;
    virtualized?: boolean;
  };
};

function ExpeditionQueueStatusBaseImpl<T>({ slots, loading, list }: ExpeditionQueueStatusBaseProps<T>) {
  list.virtualized = list.virtualized ?? true;

  const isFirstRender = useRef(true);
  const newData = useMemo(() => list.data.filter(list.filter || (() => true)), [list.data]) as T[];

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  return (
    <div className="flex flex-col gap-2 has-[>.sticky]:relative">
      {!slots.search.disable && <SearchInput {...slots.search.props} />}

      {slots.alert && (
        <div
          className={cn("flex w-full max-w-64 items-center justify-center gap-2 self-center rounded-full bg-yellow-100 px-4 py-1.5 text-xs", slots.alert.props?.className)}
          {...omitClassNameFromProps(slots.alert?.props ?? {})}
        >
          <FiAlertTriangle size={24} fill="#FFDF6C" />

          <div>{slots.alert.message}</div>
        </div>
      )}

      {!loading && (
        <section {...slots?.section?.props}>
          {newData?.length > 0 && (
            <>
              {!list.virtualized && (
                <div className="flex flex-col gap-4">
                  {list.Wrapper && list.render && list.Wrapper(newData.map(list.render))}
                  {!list.Wrapper && list.render && newData.map(list.render)}
                </div>
              )}

              {list.virtualized && (
                <div className="h-[400px]">
                  <AutoSizer>
                    {({ width, height }) => (
                      <List
                        width={width}
                        height={height}
                        rowCount={newData.length}
                        rowHeight={80}
                        rowRenderer={({ key, index, style, isScrolling, isVisible }) => {
                          const scrolling = isScrolling && !isVisible && newData.length >= 100;

                          return (
                            <div style={style} key={key}>
                              {scrolling && (
                                <div className="flex items-center justify-center">
                                  <Loader type="dots" />
                                </div>
                              )}

                              {!scrolling && (
                                <>
                                  {list.Wrapper && list.Wrapper(list.render(newData[index], index, newData))}
                                  {!list.Wrapper && list.render(newData[index], index, newData)}
                                </>
                              )}
                            </div>
                          );
                        }}
                      />
                    )}
                  </AutoSizer>
                </div>
              )}
            </>
          )}

          {newData?.length === 0 && !isFirstRender.current && (
            <div {...slots?.empty?.props}>
              <p className="text-center">{slots?.empty?.message || "Nenhum veículo encontrado."}</p>
            </div>
          )}
        </section>
      )}

      {loading && (
        <div className="flex items-center justify-center" {...slots?.loader?.wrapper}>
          <Loader type="dots" {...slots?.loader?.props} />
        </div>
      )}
    </div>
  );
}

export const ExpeditionQueueStatusBase = memo(ExpeditionQueueStatusBaseImpl) as <T>(props: ExpeditionQueueStatusBaseProps<T>) => JSX.Element;
// Configurar displayName para dev tools
Object.defineProperty(ExpeditionQueueStatusBase, "displayName", {
  value: "ExpeditionQueueStatusBase",
  writable: false,
  enumerable: false,
  configurable: true
});
